<template>
  <div class="page-container">
    <div class="page-header">
      <div class="header-left">
        <h2>股东交易趋势</h2>
        <p>展示股东交易行为趋势、FIFO 成本和持仓变动情况</p>
      </div>
    </div>

    <!-- 交易行为工具：价格 + 交易标记 + 活跃度（放在明细表格上方） -->
    <section class="kline-section">
      <div class="kline-header">
        <div class="kline-header-left">
          <div class="kline-title">股东交易行为工具</div>
          <div class="kline-desc">
            选择股东后，在价格曲线上查看其买入 / 卖出脚印，并通过热力图查看不同时间段的活跃度。
          </div>
        </div>
        <el-button type="primary" @click="handleRefresh" :disabled="calculating">
          <el-icon><Refresh /></el-icon>
          {{ calculating ? '计算中...' : '刷新数据' }}
        </el-button>
      </div>
      <div class="kline-filters">
        <el-form inline class="kline-form">
          <el-form-item label="核心股东">
            <el-select
              v-model="selectedCoreId"
              placeholder="请选择核心股东"
              clearable
              filterable
              style="min-width: 200px"
              :loading="coreOptionsLoading"
              @change="onCoreChange"
            >
              <el-option
                v-for="c in coreOptions"
                :key="c.value"
                :label="c.label"
                :value="c.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="普通股东">
            <div class="ordinary-holders-box">
              <template v-if="klineSelectedHolders.length">
                <span
                  v-for="name in klineSelectedHolders"
                  :key="name"
                  class="holder-tag"
                >
                  {{ name }}
                  <span class="holder-tag-remove" @click="removeOrdinaryHolder(name)">×</span>
                </span>
              </template>
              <span v-else class="ordinary-holders-empty">请先选择核心股东，或从下方添加</span>
              <el-dropdown
                v-if="ordinaryHoldersRest.length > 0"
                trigger="click"
                @command="addOrdinaryHolder"
              >
                <el-button type="primary" link size="small" class="add-more-btn">
                  + 添加更多（{{ ordinaryHoldersRest.length }} 个）
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item
                      v-for="name in ordinaryHoldersRest"
                      :key="name"
                      :command="name"
                    >
                      {{ name }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </el-form-item>
          <el-form-item label="时间段">
            <el-date-picker
              v-model="klineDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="YYYY-MM-DD"
              style="width: 260px"
              @change="fetchKlineData"
            />
          </el-form-item>
        </el-form>
      </div>
      <div class="kline-charts">
        <div class="kline-main-card">
          <div class="kline-main-header">
            <span class="kline-main-title">股价 + 交易标记</span>
            <span class="kline-main-subtitle">线：股价走势；点：各股东在该批次的净买入 / 净卖出（颜色区分方向，大小代表力度）</span>
          </div>
          <div
            id="tradeKlinePriceChart"
            class="trade-kline-chart"
            v-loading="klineLoading"
            element-loading-text="加载中..."
          />
        </div>
        <div class="kline-heat-card">
          <div class="kline-main-header">
            <span class="kline-main-title">交易活跃度（热力图）</span>
            <span class="kline-main-subtitle">横轴：批次；纵轴：股东；颜色表示该期净变动强度。</span>
          </div>
          <div
            id="tradeKlineHeatmapChart"
            class="trade-kline-chart small"
            v-loading="klineLoading"
            element-loading-text="加载中..."
          />
        </div>
      </div>
      <el-dialog
        v-model="klineDetailVisible"
        title="交易明细"
        width="60%"
        top="5vh"
      >
        <div v-if="klineDetailMeta.holder && klineDetailMeta.date" class="kline-detail-header">
          <div><strong>股东：</strong>{{ klineDetailMeta.holder }}</div>
          <div><strong>批次日期：</strong>{{ klineDetailMeta.date }}</div>
          <div v-if="klineDetailSummary">
            <strong>当期概览：</strong>
            <span v-if="klineDetailSummary.buyWan">买入 {{ klineDetailSummary.buyWan }} 万股</span>
            <span v-if="klineDetailSummary.buyWan && klineDetailSummary.buyAvg">（均价 {{ klineDetailSummary.buyAvg }} 元）</span>
            <span v-if="klineDetailSummary.sellWan">；卖出 {{ klineDetailSummary.sellWan }} 万股</span>
            <span v-if="klineDetailSummary.sellWan && klineDetailSummary.sellAvg">（均价 {{ klineDetailSummary.sellAvg }} 元）</span>
            <span v-if="klineDetailSummary.netWan">；净{{ klineDetailSummary.netWan > 0 ? '买入' : '卖出' }} {{ Math.abs(klineDetailSummary.netWan) }} 万股</span>
          </div>
        </div>
        <el-table
          :data="klineDetailRows"
          border
          stripe
          size="small"
          v-loading="klineDetailLoading"
        >
          <el-table-column prop="holder_name" label="股东名称" min-width="120" />
          <el-table-column prop="batch_name" label="批次" min-width="140" />
          <el-table-column prop="direction" label="方向" width="70" />
          <el-table-column prop="txn_volume" label="交易数量" width="90" align="right" />
          <el-table-column prop="txn_price" label="成交价" width="80" align="right" />
          <el-table-column prop="txn_amount" label="成交金额" width="100" align="right" />
          <el-table-column prop="curr_hold_vol" label="当期股数" width="90" align="right" />
          <el-table-column prop="fifo_cost_avg" label="FIFO成本" width="90" align="right" />
          <el-table-column prop="cash_yield" label="现金收益" width="110" align="right" />
        </el-table>
      </el-dialog>
    </section>

    <!-- 筛选条件区域（与下方明细表保持一体） -->
    <div class="filter-section">
      <el-form inline :model="filterForm" class="filter-form">
        <el-form-item label="证券账户">
          <HolderSuggestInput
            v-model="filterForm.account_keyword"
            type="raw"
            placeholder="汉字/拼音/首字母"
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item label="股东名称">
          <el-autocomplete
            v-model="filterForm.holder_keyword"
            :fetch-suggestions="fetchHolderSuggestions"
            placeholder="汉字/拼音/首字母"
            clearable
            style="width: 220px"
          >
            <template #default="{ item }">
              <span>{{ item.label || item.value }}</span>
            </template>
          </el-autocomplete>
        </el-form-item>
        <el-form-item label="核心股东">
          <HolderSuggestInput
            v-model="filterForm.core_keyword"
            type="core"
            value-key="label"
            placeholder="汉字/拼音/首字母"
            style="width: 220px"
          />
        </el-form-item>
        <el-form-item label="交易方向">
          <el-select 
            v-model="filterForm.direction" 
            placeholder="全部" 
            clearable 
            multiple
            collapse-tags
            collapse-tags-tooltip
            :max-collapse-tags="2"
            style="width: 200px"
          >
            <el-option label="买入" value="买入" />
            <el-option label="卖出" value="卖出" />
            <el-option label="无交易" value="无交易" />
          </el-select>
        </el-form-item>
        <el-form-item label="数据批次">
          <el-select v-model="filterForm.batch_keyword" placeholder="选择批次" clearable style="width: 200px">
            <el-option v-for="batch in batchOptions" :key="batch" :label="batch" :value="batch" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            查询
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 数据表格区域 -->
    <div class="table-section">
      <el-table
        :data="tableData"
        border
        stripe
        style="width: 100%"
        v-loading="loading"
        :height="tableHeight"
      >
        <!-- 第一部分：基础信息 -->
        <el-table-column prop="account_code" label="证券账户" width="120" fixed />
        <el-table-column prop="holder_name" label="持有人名称" min-width="150" fixed />
        <el-table-column prop="stock_code" label="股票代码" width="90" />
        <el-table-column prop="batch_name" label="数据批次" min-width="150" />
        <el-table-column prop="direction" label="方向" width="80">
          <template #default="{ row }">
            <el-tag :type="getDirectionType(row.direction)">{{ row.direction }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="txn_volume" label="交易数量" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.txn_volume) }}
          </template>
        </el-table-column>
        <el-table-column prop="txn_price" label="交易价格" width="90" align="right">
          <template #default="{ row }">
            {{ formatPrice(row.txn_price) }}
          </template>
        </el-table-column>
        <el-table-column prop="txn_amount" label="交易金额" width="110" align="right">
          <template #default="{ row }">
            {{ formatMoney(row.txn_amount) }}
          </template>
        </el-table-column>
        <el-table-column prop="curr_hold_vol" label="当期股数" width="100" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.curr_hold_vol) }}
          </template>
        </el-table-column>
        
        <!-- 第二部分：FIFO计算 -->
        <el-table-column prop="fifo_cost_avg" label="FIFO成本" width="95" align="right">
          <template #default="{ row }">
            <span class="fifo-cost">{{ formatPrice(row.fifo_cost_avg) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="fifo_pnl" label="FIFO盈亏" width="100" align="right">
          <template #default="{ row }">
            <span :class="getPnlClass(row.fifo_pnl)">{{ formatMoney(row.fifo_pnl) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="diluted_cost" label="摊薄成本" width="95" align="right">
          <template #default="{ row }">
            <span class="diluted-cost">{{ formatPrice(row.diluted_cost) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="diluted_pnl" label="摊薄盈亏" width="100" align="right">
          <template #default="{ row }">
            <span :class="getPnlClass(row.diluted_pnl)">{{ formatMoney(row.diluted_pnl) }}</span>
          </template>
        </el-table-column>
        
        <!-- 第三部分：现金流 -->
        <el-table-column prop="total_invest" label="总投入" width="110" align="right">
          <template #default="{ row }">
            {{ formatMoney(row.total_invest) }}
          </template>
        </el-table-column>
        <el-table-column prop="total_retrieve" label="总回收" width="110" align="right">
          <template #default="{ row }">
            {{ formatMoney(row.total_retrieve) }}
          </template>
        </el-table-column>
        <el-table-column prop="cash_yield" label="现金收益" width="100" align="right">
          <template #default="{ row }">
            <span :class="getPnlClass(row.cash_yield)">{{ formatMoney(row.cash_yield) }}</span>
          </template>
        </el-table-column>
        
        <!-- 第四部分：市值+关联 -->
        <el-table-column prop="hold_market_val" label="持仓市值" width="110" align="right">
          <template #default="{ row }">
            <span class="market-value">{{ formatMoney(row.hold_market_val) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="core_holder_name" label="核心股东" min-width="120" />
        <el-table-column prop="control_type" label="控制类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getControlTypeTag(row.control_type)" size="small">
              {{ getControlTypeText(row.control_type) }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <div class="pagination-info">
          共 {{ pagination.total }} 条记录
        </div>
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="pagination.pageSize"
          layout="sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'
import * as echarts from 'echarts'

// 筛选条件
const filterForm = reactive({
  account_keyword: '',
  holder_keyword: '',
  core_keyword: '',
  batch_keyword: '',
  direction: []
})

// 表格数据
const tableData = ref([])
const loading = ref(false)
const calculating = ref(false)
const batchOptions = ref([])
const tableHeight = ref(600)

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 50,
  total: 0
})

// ======== 交易行为工具：状态 ========
const tradePenetrationDate = ref('') // 用于拉取核心股东及关联名单的批次日期
const coreOptions = ref([])
const coreOptionsLoading = ref(false)
const selectedCoreId = ref('')
const relatedOrdinaryHolders = ref([]) // 当前核心股东下全部关联普通股东（来自穿透详情）
const klineHolderOptions = ref([])
const klineSelectedHolders = ref([]) // 已选普通股东（用于图表），默认 10 个，可删可加
const DEFAULT_ORDINARY_COUNT = 10
const klineLimit = ref(0) // 0 表示按时间段
const klineDateRange = ref([]) // [start, end] YYYY-MM-DD
const klineLoadingHolders = ref(false)
const klineLoading = ref(false)
const klineDates = ref([])
const klinePrice = ref([])
const klineSeries = ref([])
const klineDetailVisible = ref(false)
const klineDetailMeta = reactive({ holder: '', date: '' })
const klineDetailRows = ref([])
const klineDetailLoading = ref(false)
const klineDetailSummary = computed(() => {
  const rows = klineDetailRows.value || []
  if (!rows.length) return null
  let buyVol = 0
  let buyAmt = 0
  let sellVol = 0
  let sellAmt = 0
  rows.forEach(r => {
    const dir = r.direction
    const vol = Number(r.txn_volume || 0)
    const price = Number(r.txn_price || 0)
    if (!vol || !price) return
    if (dir === '买入') {
      buyVol += vol
      buyAmt += vol * price
    } else if (dir === '卖出') {
      sellVol += vol
      sellAmt += vol * price
    }
  })
  const toWan = v => Number((v / 10000).toFixed(2))
  const buyWan = buyVol ? toWan(buyVol) : 0
  const sellWan = sellVol ? toWan(sellVol) : 0
  const netWan = Number((buyWan - sellWan).toFixed(2))
  const buyAvg = buyVol ? Number((buyAmt / buyVol).toFixed(2)) : null
  const sellAvg = sellVol ? Number((sellAmt / sellVol).toFixed(2)) : null
  return { buyWan, sellWan, netWan, buyAvg, sellAvg }
})

let klinePriceChart = null
let klineHeatmapChart = null

const ensureKlineCharts = () => {
  const priceDom = document.getElementById('tradeKlinePriceChart')
  const heatDom = document.getElementById('tradeKlineHeatmapChart')
  if (priceDom && !klinePriceChart) {
    klinePriceChart = echarts.init(priceDom)
  }
  if (heatDom && !klineHeatmapChart) {
    klineHeatmapChart = echarts.init(heatDom)
  }
}

const buildKlinePriceOption = () => {
  const dates = klineDates.value
  const price = klinePrice.value
  const series = []

  series.push({
    name: '股价',
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 4,
    lineStyle: { width: 2, color: '#0f766e' },
    itemStyle: { color: '#0f766e' },
    data: price
  })

  klineSeries.value.forEach((s, idx) => {
    const deltas = s.deltas || []
    const label = s.holder || s.account || `股东${idx + 1}`

    const buyPoints = []
    const sellPoints = []
    dates.forEach((d, i) => {
      const p = price[i]
      const delta = deltas[i] ?? 0
      if (!p && p !== 0) return
      if (!delta) return
      const basePoint = {
        value: [d, p],
        delta,
        holder: label
      }
      if (delta > 0) buyPoints.push(basePoint)
      else if (delta < 0) sellPoints.push(basePoint)
    })

    if (buyPoints.length) {
      series.push({
        name: label,
        type: 'scatter',
        symbol: 'triangle',
        symbolSize: params => {
          const data = params && params.data ? params.data : {}
          const d = data.delta || 0
          const base = 10
          return base + Math.min(26, Math.abs(d) * 1.0)
        },
        itemStyle: {
          color: '#16a34a',
          borderColor: '#ffffff',
          borderWidth: 2,
          shadowBlur: 12,
          shadowColor: 'rgba(22,163,74,0.7)'
        },
        z: 5,
        data: buyPoints
      })
    }

    if (sellPoints.length) {
      series.push({
        name: label,
        type: 'scatter',
        symbol: 'triangle',
        symbolRotate: 180,
        symbolSize: params => {
          const data = params && params.data ? params.data : {}
          const d = data.delta || 0
          const base = 10
          return base + Math.min(26, Math.abs(d) * 1.0)
        },
        itemStyle: {
          color: '#dc2626',
          borderColor: '#ffffff',
          borderWidth: 2,
          shadowBlur: 12,
          shadowColor: 'rgba(220,38,38,0.7)'
        },
        z: 5,
        data: sellPoints
      })
    }
  })

  return {
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        const items = Array.isArray(params) ? params : [params]
        if (!items.length) return ''
        const date = items[0].axisValueLabel
        const lines = [`${date}`]
        items.forEach(p => {
          if (p.seriesName === '股价') {
            const v = p.data
            if (v != null) {
              lines.push(`股价：${v.toFixed ? v.toFixed(2) : v} 元`)
            }
          } else if (p.data && typeof p.data.delta === 'number') {
            const d = p.data.delta
            if (d !== 0) {
              const dir = d > 0 ? '净买入' : '净卖出'
              lines.push(`${p.data.holder || p.seriesName}：${dir} ${Math.abs(d)} 万股`)
            }
          }
        })
        return lines.join('<br/>')
      }
    },
    legend: {
      data: ['股价', ...klineSeries.value.map(s => s.holder || s.account || '股东')],
      top: 0
    },
    grid: {
      left: '5%',
      right: '5%',
      top: 40,
      bottom: 20,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dates
    },
    yAxis: {
      type: 'value',
      name: '价格（元）',
      axisLabel: { formatter: '{value}' }
    },
    series
  }
}

const buildKlineHeatmapOption = () => {
  const dates = klineDates.value
  const holders = klineSeries.value.map(s => s.holder || s.account || '股东')
  const data = []
  klineSeries.value.forEach((s, hi) => {
    const deltas = s.deltas || []
    deltas.forEach((d, i) => {
      data.push([i, hi, Math.abs(d || 0)])
    })
  })

  return {
    tooltip: {
      position: 'top',
      formatter: params => {
        const [x, y, v] = params.data
        const date = dates[x]
        const holder = holders[y]
        return `${date}<br/>${holder}<br/>活跃度（按净变动绝对值）：${v}`
      }
    },
    grid: {
      left: '10%',
      right: '8%',
      top: 40,
      bottom: 20,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dates,
      splitArea: { show: false }
    },
    yAxis: {
      type: 'category',
      data: holders,
      splitArea: { show: false }
    },
    visualMap: {
      min: 0,
      max: 40,
      calculable: false,
      orient: 'vertical',
      right: 0,
      top: 50,
      inRange: {
        color: ['#e5e7eb', '#60a5fa', '#1d4ed8']
      },
      text: ['高', '低'],
      show: true
    },
    series: [
      {
        name: '活跃度',
        type: 'heatmap',
        data,
        label: { show: false }
      }
    ]
  }
}

const renderKlineCharts = () => {
  ensureKlineCharts()
  if (klinePriceChart) {
    klinePriceChart.setOption(buildKlinePriceOption(), true)

    // 点击单点拉详情（按日期聚合：同一日期有多个股东时，右侧展示所有相关股东的明细）
    klinePriceChart.off('click')
    klinePriceChart.on('click', params => {
      if (!params || params.seriesName === '股价') return
      const date = params.name || (Array.isArray(params.value) ? params.value[0] : '')
      if (!date) return
      const idx = klineDates.value.indexOf(date)
      if (idx < 0) return
      const activeHolders = (klineSeries.value || [])
        .filter(s => {
          const d = (s.deltas || [])[idx]
          return d && d !== 0
        })
        .map(s => s.holder || s.account || '')
        .filter(Boolean)
      if (!activeHolders.length) return
      if (activeHolders.length === 1) {
        klineDetailMeta.holder = activeHolders[0]
      } else {
        klineDetailMeta.holder = `${activeHolders[0]} 等 ${activeHolders.length} 人`
      }
      klineDetailMeta.date = date
      fetchKlinePointDetailForHolders(activeHolders, date)
    })
  }
  if (klineHeatmapChart) {
    klineHeatmapChart.setOption(buildKlineHeatmapOption(), true)
  }
}

// 当前核心下「未选入图表」的普通股东（供「添加更多」下拉）
const ordinaryHoldersRest = computed(() => {
  const selected = klineSelectedHolders.value || []
  return (relatedOrdinaryHolders.value || []).filter(name => !selected.includes(name))
})

// 拉取核心股东列表（依赖批次日期；每次请求，不使用页内缓存）
const fetchCoreOptions = async () => {
  if (!tradePenetrationDate.value) {
    try {
      const dateRes = await axios.get('/api/penetration/dates/')
      if (dateRes.data.status === 'ok' && dateRes.data.data?.length) {
        tradePenetrationDate.value = dateRes.data.data[0].value
      }
    } catch (e) {
      console.warn('获取穿透日期失败', e)
    }
    if (!tradePenetrationDate.value) {
      const batchRes = await axios.get('/api/transaction_detail/batches/')
      if (batchRes.data.status === 'ok' && batchRes.data.data?.length) {
        const first = batchRes.data.data[0]
        tradePenetrationDate.value = first.date || first.value || ''
      }
    }
  }
  const date = tradePenetrationDate.value
  if (!date) {
    coreOptions.value = []
    return
  }
  coreOptionsLoading.value = true
  try {
    const res = await axios.get('/api/penetration/core-shareholders/', { params: { date } })
    if (res.data.status === 'ok' && Array.isArray(res.data.data)) {
      coreOptions.value = res.data.data.map(c => ({
        value: c.core_id,
        label: c.name || c.core_id
      }))
    } else {
      coreOptions.value = []
    }
  } catch (e) {
    console.error('获取核心股东列表失败', e)
    coreOptions.value = []
  } finally {
    coreOptionsLoading.value = false
  }
}

// 选择核心股东后拉取其下关联普通股东，默认选前 10 个
const onCoreChange = async () => {
  const coreId = selectedCoreId.value
  relatedOrdinaryHolders.value = []
  klineSelectedHolders.value = []
  if (!coreId) {
    fetchKlineData()
    return
  }
  try {
    const date = tradePenetrationDate.value || ''
    const res = await axios.get(`/api/penetration/core-shareholder/${coreId}/`, { params: { date } })
    if (res.data.status !== 'ok' || !res.data.data) {
      const corePick = coreOptions.value.find(c => c.value === coreId)
      const fallback = (corePick?.label || '').trim()
      if (fallback) {
        relatedOrdinaryHolders.value = [fallback]
        klineSelectedHolders.value = [fallback]
        await fetchKlineData()
      } else {
        fetchKlineData()
      }
      return
    }
    const detail = res.data.data
    const names = new Set()
    ;(detail.full_control_details || []).forEach(d => {
      if (d.holder_name) names.add(d.holder_name)
    })
    ;(detail.indirect_details || []).forEach(d => {
      if (d.holder_name) names.add(d.holder_name)
    })
    const corePick = coreOptions.value.find(c => c.value === coreId)
    const coreName = (detail.name || corePick?.label || '').trim()
    if (coreName) names.add(coreName)
    const all = Array.from(names)
    const rest = coreName ? all.filter(h => h !== coreName) : all
    relatedOrdinaryHolders.value = coreName ? [coreName, ...rest] : all
    klineSelectedHolders.value = relatedOrdinaryHolders.value.slice(0, DEFAULT_ORDINARY_COUNT)
    await fetchKlineData()
  } catch (e) {
    console.error('获取核心股东关联名单失败', e)
    const corePick = coreOptions.value.find(c => c.value === coreId)
    const fallback = (corePick?.label || '').trim()
    if (fallback) {
      relatedOrdinaryHolders.value = [fallback]
      klineSelectedHolders.value = [fallback]
      await fetchKlineData()
    }
  }
}

const removeOrdinaryHolder = (name) => {
  klineSelectedHolders.value = klineSelectedHolders.value.filter(h => h !== name)
  fetchKlineData()
}

const addOrdinaryHolder = (name) => {
  if (!klineSelectedHolders.value.includes(name)) {
    klineSelectedHolders.value = [...klineSelectedHolders.value, name]
    fetchKlineData()
  }
}

// 获取 K 线数据
const fetchKlineData = async () => {
  if (!klineSelectedHolders.value.length) return
  klineLoading.value = true
  try {
    const holdersParam = klineSelectedHolders.value.join(',')
    const params = {
      holders: holdersParam,
      limit_batches: klineLimit.value || ''
    }
    if (klineDateRange.value && klineDateRange.value.length === 2) {
      params.start_date = klineDateRange.value[0]
      params.end_date = klineDateRange.value[1]
    }
    const res = await axios.get('/api/transaction_detail/kline/', { params })
    if (res.data.status === 'ok') {
      const data = res.data.data || {}
      klineDates.value = data.dates || []
      klinePrice.value = data.price || []
      klineSeries.value = data.series || []
      renderKlineCharts()
    }
  } catch (e) {
    console.error('获取交易 K 线数据失败', e)
  } finally {
    klineLoading.value = false
  }
}

// 获取某日期多个股东的具体交易明细
const fetchKlinePointDetailForHolders = async (holders, date) => {
  if (!holders || !holders.length || !date) return
  klineDetailLoading.value = true
  klineDetailVisible.value = true
  try {
    const requests = holders.map(h =>
      axios.get('/api/transaction_detail/point/', {
        params: { holder: h, date }
      })
    )
    const results = await Promise.allSettled(requests)
    const allRows = []
    results.forEach(r => {
      if (r.status === 'fulfilled' && r.value.data && r.value.data.status === 'ok') {
        allRows.push(...(r.value.data.data || []))
      }
    })
    klineDetailRows.value = allRows
  } catch (e) {
    console.error('获取交易点明细失败', e)
  } finally {
    klineDetailLoading.value = false
  }
}

// 获取数据
const fetchData = async () => {
  loading.value = true
  try {
    // 处理多选的direction参数
    const directionParam = filterForm.direction.length > 0 ? filterForm.direction.join(',') : ''
    
    const params = {
      page: pagination.currentPage,
      page_size: pagination.pageSize,
      account_keyword: filterForm.account_keyword,
      holder_keyword: filterForm.holder_keyword,
      core_keyword: filterForm.core_keyword,
      batch_keyword: filterForm.batch_keyword,
      direction: directionParam
    }
    
    const response = await axios.get('/api/transaction_detail/', { params })
    if (response.data.status === 'ok') {
      tableData.value = response.data.data
      pagination.total = response.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('获取数据失败: ' + error.message)
  } finally {
    loading.value = false
  }
}

// 股东名称联想（统一联想搜索：调用 unique_shareholder/search，选中后传姓名给列表接口）
const fetchHolderSuggestions = (queryString, cb) => {
  const keyword = (queryString || '').trim()
  if (!keyword) {
    cb([])
    return
  }
  axios.get('/api/unique_shareholder/search/', { params: { keyword } })
    .then(res => {
      if (res.data.status !== 'ok') {
        cb([])
        return
      }
      const list = res.data.data || []
      const seen = new Set()
      const suggestions = []
      for (const item of list) {
        const name = (item.name || item.label || '').trim()
        if (name && !seen.has(name)) {
          seen.add(name)
          suggestions.push({ value: name, label: item.label || name })
        }
      }
      cb(suggestions.slice(0, 20))
    })
    .catch(() => cb([]))
}

// 获取批次列表（每次请求）
const fetchBatches = async () => {
  try {
    const response = await axios.get('/api/transaction_detail/batches/')
    if (response.data.status === 'ok') {
      batchOptions.value = response.data.data.map(b => b.value)
    }
  } catch (error) {
    console.error('获取批次失败:', error)
  }
}

// 刷新数据：与股权穿透一致，顶部提示 + 按钮「计算中...」不可点
const handleRefresh = async () => {
  if (calculating.value) return
  calculating.value = true
  ElMessage.info('正在重新计算数据，请稍候...')
  try {
    const response = await axios.post('/api/transaction_detail/calculate/')
    if (response.data.status === 'ok') {
      ElMessage.success('已按最新数据重新计算')
      await fetchBatches()
      await fetchCoreOptions()
      fetchData()
      if (selectedCoreId.value) {
        await onCoreChange()
      } else {
        fetchKlineData()
      }
    } else {
      ElMessage.error(response.data.message || '计算失败')
    }
  } catch (error) {
    ElMessage.error('计算失败: ' + error.message)
  } finally {
    calculating.value = false
  }
}

// 查询按钮
const handleSearch = () => {
  pagination.currentPage = 1
  fetchData()
}

// 重置按钮
const handleReset = () => {
  filterForm.account_keyword = ''
  filterForm.holder_keyword = ''
  filterForm.core_keyword = ''
  filterForm.batch_keyword = ''
  filterForm.direction = []
  pagination.currentPage = 1
  fetchData()
}

// 分页相关方法
const handleSizeChange = (val) => {
  pagination.pageSize = val
  fetchData()
}

const handleCurrentChange = (val) => {
  pagination.currentPage = val
  fetchData()
}

// 格式化方法
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-'
  return new Intl.NumberFormat('zh-CN').format(num)
}

const formatPrice = (price) => {
  if (price === null || price === undefined) return '-'
  return Number(price).toFixed(4)
}

const formatMoney = (money) => {
  if (money === null || money === undefined) return '-'
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }).format(money)
}

// 样式方法
const getDirectionType = (direction) => {
  const types = {
    '买入': 'danger',
    '卖出': 'success',
    '无交易': 'info'
  }
  return types[direction] || 'info'
}

const getPnlClass = (value) => {
  if (!value || value === 0) return ''
  return value > 0 ? 'profit' : 'loss'
}

const getControlTypeTag = (type) => {
  // 英文到中文的映射；账户控制沿用原直接控制样式
  const typeMap = {
    'direct': '直接控制',
    'account_control': '账户控制',
    'indirect': '间接控制',
    'consistent': '一致行动',
    'self': '自身账户',
    '直接控制': 'danger',
    '账户控制': 'danger',
    '一致行动': 'warning',
    '间接控制': '',
    '自身账户': 'success'
  }
  
  // 如果是标签类型查询（danger/warning等）
  if (['danger', 'warning', 'success', 'info', ''].includes(type)) {
    return type
  }
  
  // 否则返回中文对应的标签类型
  return typeMap[type] || 'info'
}

// 获取控制类型的中文显示
const getControlTypeText = (type) => {
  const textMap = {
    'direct': '账户控制',
    'account_control': '账户控制',
    'indirect': '间接控制',
    'consistent': '一致行动',
    'self': '自身账户',
    '直接控制': '账户控制',
    '账户控制': '账户控制',
    '一致行动': '一致行动',
    '间接控制': '间接控制',
    '自身账户': '自身账户'
  }
  return textMap[type] || type || '未匹配'
}

// 初始化：首屏自动带一页数据 + 核心股东列表，默认选中「潘彩云」，K 线在选核心后拉取
onMounted(async () => {
  tableHeight.value = window.innerHeight - 350
  await fetchBatches()
  fetchData()
  // 核心股东下拉（用于交易行为工具）
  await fetchCoreOptions()
  if (!selectedCoreId.value && coreOptions.value.length) {
    const target = coreOptions.value.find(c => (c.label || '').includes('潘彩云'))
    if (target) {
      selectedCoreId.value = target.value
      await onCoreChange()
    }
  }
  const today = new Date()
  const endStr = today.toISOString().slice(0, 10)
  const lastYear = new Date(today.getTime() - 365 * 24 * 3600 * 1000)
  const startStr = lastYear.toISOString().slice(0, 10)
  klineDateRange.value = [startStr, endStr]
})

onBeforeUnmount(() => {
  if (klinePriceChart) {
    klinePriceChart.dispose()
    klinePriceChart = null
  }
  if (klineHeatmapChart) {
    klineHeatmapChart.dispose()
    klineHeatmapChart = null
  }
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.header-left p {
  margin: 0;
  color: #606266;
}

.filter-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.table-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.kline-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}
.kline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.kline-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.kline-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.kline-desc {
  font-size: 12px;
  color: #6b7280;
}
.kline-filters {
  margin-bottom: 10px;
}
.kline-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 16px;
}
.ordinary-holders-box {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 4px 0;
}
.holder-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  font-size: 13px;
  color: #065f46;
}
.holder-tag-remove {
  cursor: pointer;
  color: #047857;
  font-size: 16px;
  line-height: 1;
  padding-left: 2px;
}
.holder-tag-remove:hover {
  color: #dc2626;
}
.ordinary-holders-empty {
  color: #9ca3af;
  font-size: 13px;
}
.add-more-btn {
  margin-left: 4px;
}
.kline-charts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.kline-main-card,
.kline-heat-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
}
.kline-main-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
}
.kline-main-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.kline-main-subtitle {
  font-size: 12px;
  color: #9ca3af;
}
.trade-kline-chart {
  width: 100%;
  height: 320px;
}
.trade-kline-chart.small {
  height: 260px;
}
.kline-detail-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #4b5563;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-info {
  color: #606266;
  font-size: 14px;
}

/* 颜色样式 */
.fifo-cost {
  color: #67c23a;
  font-weight: 500;
}

.diluted-cost {
  color: #f56c6c;
  font-weight: 500;
}

.market-value {
  color: #9b59b6;
  font-weight: 500;
}

.profit {
  color: #d9001b;
}

.loss {
  color: #52c41a;
}
</style>
